import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SocialSidebar from '@/components/feature/SocialSidebar';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { SERVICES_DATA } from './data/servicesData';

interface ServicesIndexPageProps {
  lang: 'en' | 'tr';
}

export default function ServicesIndexPage({ lang }: ServicesIndexPageProps) {
  const isEN = lang === 'en';
  const basePath = isEN ? '/services' : '/tr/hizmetler';
  const homePath = isEN ? '/' : '/tr';

  useSeoMeta({
    lang,
    canonicalPath: basePath,
    title: isEN
      ? 'Event Services Istanbul Budapest Dubai | CSG Can ÖZEN Technical Production'
      : 'Etkinlik Hizmetleri İstanbul Budapeşte Dubai | CSG Can ÖZEN Teknik Prodüksiyon',
    description: isEN
      ? 'Complete event services: technical production, AV, stage design, corporate events, DMC, hybrid events, product launches, award ceremonies. 35+ years. Istanbul, Budapest, Dubai.'
      : 'Eksiksiz etkinlik hizmetleri: teknik prodüksiyon, AV, sahne tasarımı, kurumsal etkinlikler, DMC, hibrit etkinlikler, ürün lansmanları, ödül törenleri. 35+ yıl. İstanbul, Budapeşte, Dubai.',
    keywords: isEN
      ? 'event services Istanbul, AV production Turkey, corporate event company Istanbul, technical production Budapest Dubai'
      : 'etkinlik hizmetleri İstanbul, AV prodüksiyon Türkiye, kurumsal etkinlik şirketi İstanbul, teknik prodüksiyon Budapeşte Dubai, kurumsal etkinlik prodüksiyon İstanbul',
    ogLocale: isEN ? 'en_US' : 'tr_TR',
  });

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SocialSidebar lang={lang} />
      <Header lang={lang} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to={homePath} className="hover:text-yellow-400 transition-colors">
              {isEN ? 'Home' : 'Ana Sayfa'}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">{isEN ? 'Services' : 'Hizmetler'}</span>
          </nav>
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-yellow-400 text-sm font-semibold uppercase tracking-widest mb-3">
              {isEN ? 'What We Do' : 'Ne Yapıyoruz'}
            </p>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {isEN ? 'Our ' : 'Hizmet '}
              <span className="text-yellow-400">{isEN ? 'Services' : 'Portföyümüz'}</span>
            </h1>
            <p className="text-xl text-gray-300">
              {isEN
                ? 'From concept to execution — comprehensive event solutions across 17+ countries.'
                : 'Konseptten uygulamaya — 17\'den fazla ülkede kapsamlı etkinlik çözümleri.'}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {SERVICES_DATA.map((service) => {
              const data = isEN ? service.en : service.tr;
              return (
                <Link
                  key={service.slug}
                  to={`${basePath}/${service.slug}`}
                  className="group bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-400/50 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={service.heroImage}
                      alt={data.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-3 left-3">
                      <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                        <i className={`${service.icon} text-lg text-black`} />
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="text-base font-semibold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                      {data.title}
                    </h2>
                    <p className="text-gray-400 text-sm leading-relaxed mb-3">{data.description}</p>
                    <span className="text-yellow-400 text-xs font-semibold flex items-center gap-1">
                      {isEN ? 'Learn more' : 'Detaylar'}
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-yellow-400/10 to-yellow-500/5 border-y border-yellow-400/20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {isEN ? 'Ready to Plan Your Next Event?' : 'Bir Sonraki Etkinliğinizi Planlamaya Hazır mısınız?'}
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            {isEN
              ? 'Contact us for a custom quote tailored to your specific event needs.'
              : 'Etkinlik ihtiyaçlarınıza özel teklif için bizimle iletişime geçin.'}
          </p>
          <Link
            to={`${homePath}#contact`}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-send-plane-line" />
            {isEN ? 'Request a Quote' : 'Teklif Talep Et'}
          </Link>
        </div>
      </section>

      <Footer lang={lang} />
    </div>
  );
}
